import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { companiesApi } from '@/services/api';
import { Company } from '@/types/client';
import { useScope } from '@/hooks/useScope';
import { WRITE_GENERAL_MASTERS } from '@/constants/scopeCodes';
import PageCard, { DetailSection, DetailField } from '@/components/utils/PageCard';
import { Tooltip } from '@mui/material';
import { PageFrame, Button, ActiveStatus, YesNo, Note, Placeholder, confirmColors } from '@/components/ui';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import Swal from 'sweetalert2';

const CompanyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canWrite = useScope(WRITE_GENERAL_MASTERS);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (id) {
      fetchCompany(parseInt(id));
    }
  }, [id]);

  const fetchCompany = async (companyId: number) => {
    try {
      setLoading(true);
      const data = await companiesApi.getCompany(companyId);
      setCompany(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch company details');
      console.error('Error fetching company:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!company) return;
    const result = await Swal.fire({
      title: 'Activate Company?',
      text: `Do you want to activate ${company.name}?`,
      icon: 'question',
      showCancelButton: true,
      ...confirmColors('success'),
      confirmButtonText: 'Yes, Activate',
    });
    if (!result.isConfirmed) return;
    try {
      setActionLoading(true);
      await companiesApi.activateCompany(company.id);
      await fetchCompany(company.id);
      Swal.fire('Activated!', `${company.name} has been activated.`, 'success');
    } catch (err) {
      console.error('Error activating company:', err);
      Swal.fire('Error', 'Failed to activate company.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (!company) return;
    const result = await Swal.fire({
      title: 'Deactivate Company?',
      text: `Do you want to deactivate ${company.name}?`,
      icon: 'warning',
      showCancelButton: true,
      ...confirmColors('danger'),
      confirmButtonText: 'Yes, Deactivate',
    });
    if (!result.isConfirmed) return;
    try {
      setActionLoading(true);
      await companiesApi.deactivateCompany(company.id);
      await fetchCompany(company.id);
      Swal.fire('Deactivated!', `${company.name} has been deactivated.`, 'success');
    } catch (err) {
      console.error('Error deactivating company:', err);
      Swal.fire('Error', 'Failed to deactivate company.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <PageFrame header={null}>
        <Placeholder kind="loading" />
      </PageFrame>
    );
  }

  if (error) {
    return (
      <PageFrame header={null}>
        <Note tone="error">{error}</Note>
      </PageFrame>
    );
  }

  if (!company) {
    return (
      <PageFrame header={null}>
        <Note tone="warning">Company not found.</Note>
      </PageFrame>
    );
  }

  const actions = (
    <>
      <Tooltip title="Back to list">
        <Button
          iconOnly
          onClick={() => navigate('/companies')}
          aria-label="Back to list"
        >
          <ArrowBackIcon fontSize="small" />
        </Button>
      </Tooltip>

      {canWrite && (
        <Tooltip title="Edit">
          <Button
            iconOnly
            onClick={() => navigate(`/companies/${company.id}/edit`)}
            aria-label="Edit"
          >
            <EditIcon fontSize="small" />
          </Button>
        </Tooltip>
      )}

      {canWrite && (company.is_active ? (
        <Button
          variant="danger"
          icon={<BlockIcon fontSize="small" />}
          onClick={handleDeactivate}
          disabled={actionLoading}
          size="sm"
        >
          {actionLoading ? 'Processing…' : 'Deactivate'}
        </Button>
      ) : (
        <Button
          variant="success"
          icon={<CheckCircleIcon fontSize="small" />}
          onClick={handleActivate}
          disabled={actionLoading}
          size="sm"
        >
          {actionLoading ? 'Processing…' : 'Activate'}
        </Button>
      ))}
    </>
  );

  return (
    <PageCard
      title={`${company.symbol} — ${company.name}`}
      subtitle={<ActiveStatus active={!!company.is_active} />}
      actions={actions}
    >
      <DetailSection title="Basic Information">
        <DetailField label="Company Name" value={company.name} />
        <DetailField label="Symbol" value={company.symbol} mono />
        <DetailField label="ISIN" value={company.isin} mono />
        <DetailField label="BSE Code" value={company.bse_code} />
        <DetailField label="NSE Code" value={company.nse_code} />
        <DetailField label="Accord Code" value={company.accord_code} />
        <DetailField label="MF Scheme Code" value={company.mf_scheme_code} />
        <DetailField label="Alpha Sense Code" value={company.alpha_sense_code} />
        <DetailField
          label="Country"
          value={company.country ? `${company.country.name} (${company.country.code})` : undefined}
        />
      </DetailSection>

      <DetailSection title="Classification">
        <DetailField label="Asset Class" value={company.asset_class?.name || company.asset_class_name} />
        <DetailField label="Category" value={company.category?.category || company.category_name} />
        <DetailField label="Sector" value={company.sector?.sector_name || company.sector_name} />
      </DetailSection>

      <DetailSection title="Trading Information">
        <DetailField label="NRI Restriction" value={<YesNo value={!!company.restriction_for_nri} />} />
        <DetailField label="Exclude from AUM Calculation" value={<YesNo value={!!company.exclude_from_aum_calculation} />} />
      </DetailSection>
    </PageCard>
  );
};

export default CompanyDetail;
